import { Link, useLocation, useNavigate } from "react-router-dom";
import { Compass, Plus, BookMarked, HelpCircle, LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import crown from "@/assets/crown.png";

const NAV_ITEMS = [
  { to: "/circle", label: "Browse", icon: Compass, testid: "nav-browse" },
  { to: "/create", label: "Create", icon: Plus, testid: "nav-create" },
  { to: "/my-plans", label: "My Plans", icon: BookMarked, testid: "nav-my-plans" },
];

export const AppShell = ({ children, wide = false }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#0B0B0F]">
      <header className="sticky top-0 z-40 bg-[#0B0B0F]/80 backdrop-blur-xl border-b border-[#D4AF37]/20">
        <div className="max-w-lg mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/circle" className="flex items-center gap-2.5" data-testid="header-brand">
            <img src={crown} alt="" className="w-8 h-8" />
            <span className="font-serif font-bold text-lg tracking-wide text-[#D4AF37]">
              THE CIRCLE
            </span>
          </Link>
          <div className="flex items-center gap-1">
            {user?.is_admin && (
              <Link
                to="/admin-control"
                data-testid="header-admin-link"
                className="p-2 text-[#A0A0A0] hover:text-[#D4AF37] transition-colors"
                title="Admin Control"
              >
                <ShieldCheck className="w-5 h-5" strokeWidth={1.5} />
              </Link>
            )}
            <Link
              to="/how-it-works"
              data-testid="header-how-it-works-link"
              className="p-2 text-[#A0A0A0] hover:text-[#D4AF37] transition-colors"
              title="How It Works"
            >
              <HelpCircle className="w-5 h-5" strokeWidth={1.5} />
            </Link>
            {user && (
              <Avatar className="w-8 h-8 border border-[#D4AF37]/40 mx-1" data-testid="header-avatar">
                <AvatarImage src={user.photo} alt={user.name} />
                <AvatarFallback className="bg-[#15151A] text-[#D4AF37] text-xs">
                  {user.name?.[0] || "?"}
                </AvatarFallback>
              </Avatar>
            )}
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="p-2 text-[#A0A0A0] hover:text-[#D4AF37] transition-colors"
              title="Sign out"
            >
              <LogOut className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <main className={`${wide ? "max-w-4xl" : "max-w-lg"} mx-auto px-5 pt-8 pb-32`}>
        {children}
        <div className="mt-12 pt-6 border-t border-white/5 flex items-center justify-center gap-4 text-[11px] text-[#5a5a66]">
          <Link to="/terms" className="hover:text-[#D4AF37] transition-colors">Terms of Service</Link>
          <span>·</span>
          <Link to="/privacy" className="hover:text-[#D4AF37] transition-colors">Privacy Policy</Link>
        </div>
      </main>

      <nav className="fixed bottom-0 inset-x-0 z-40 bg-[#0B0B0F]/85 backdrop-blur-xl border-t border-[#D4AF37]/20">
        <div className="max-w-lg mx-auto flex items-stretch justify-around h-16">
          {NAV_ITEMS.map(({ to, label, icon: Icon, testid }) => {
            const active = location.pathname === to;
            return (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                className={`flex flex-col items-center justify-center gap-1 px-6 transition-colors ${
                  active ? "text-[#D4AF37]" : "text-[#A0A0A0] hover:text-white"
                }`}
              >
                <Icon className="w-5 h-5" strokeWidth={active ? 2 : 1.5} />
                <span className="text-[10px] uppercase tracking-widest font-medium">{label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};
